import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { BuiltBuddyAvatar } from "@/components/builtbuddy/BuiltBuddyAvatar";

/**
 * Greeting speech bubble above the closed BuiltBuddy launcher — "Hi, I'm Buddy — your personal engineer."
 * Appears GREETING_DELAY_MS after the page loads with a soft fade-in and a gentle pulse, and goes away for the rest
 * of the browser session (sessionStorage) as soon as the user opens BuiltBuddy or dismisses it.
 */
export const GREETING_DELAY_MS = 3000;
export const GREETING_DONE_KEY = "bb_greeting_done";

export const greetingDone = () => {
  try {
    return sessionStorage.getItem(GREETING_DONE_KEY) === "1";
  } catch {
    return false;
  }
};

export const markGreetingDone = () => {
  try {
    sessionStorage.setItem(GREETING_DONE_KEY, "1");
  } catch {
    // storage unavailable — the bubble still hides for this page
  }
};

/**
 * Show-after-3-seconds logic shared by the global and the job-page launcher.
 * `open` = the chat / job picker is open → the greeting hides and is marked done for the session.
 */
export const useGreeting = ({ open = false, enabled = true } = {}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled || greetingDone()) return undefined;
    const t = setTimeout(() => {
      if (!greetingDone()) setVisible(true);
    }, GREETING_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    markGreetingDone();
    setVisible(false);
  }, [open]);

  const dismiss = () => {
    markGreetingDone();
    setVisible(false);
  };

  return { visible: visible && enabled && !open, dismiss };
};

/**
 * The bubble itself. Absolutely positioned above the launcher inside the fixed slot (`corner` flips the tail).
 * `preview` renders it inline and static for Settings › Chat widget.
 */
export const BuiltBuddyGreeting = ({ text, avatar = "engineer", corner = "right", onOpen, onDismiss, preview = false, className = "" }) => {
  const right = corner !== "left";
  const tid = preview ? "builtbuddy-greeting-preview" : "builtbuddy-greeting";
  return (
    <div
      className={`bb-greeting ${preview ? "relative" : `absolute bottom-[calc(100%+14px)] ${right ? "right-0" : "left-0"}`} w-max max-w-[min(280px,calc(100vw-3rem))] ${className}`}
      role="status"
      aria-live="polite"
      data-testid={tid}
      data-corner={corner}
    >
      {/* the bubble (and its tail) pulse; the dismiss button sits outside the animated box so it stays a steady target */}
      <button
        type="button"
        onClick={onOpen}
        className={`bb-greeting-bubble group relative flex w-full items-center gap-2.5 rounded-2xl border border-border bg-card/95 py-2.5 pl-2.5 pr-4 text-left shadow-[var(--shadow-popover)] backdrop-blur-md transition-[border-color] duration-150 hover:border-brand-steel/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-steel focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
          right ? "origin-bottom-right" : "origin-bottom-left"
        }`}
        aria-label={`${text} Open BuiltBuddy`}
        data-testid={`${tid}-open`}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-brand-steel/40 bg-brand-navy" aria-hidden>
          <BuiltBuddyAvatar style={avatar} animated={!preview} className="h-full w-full" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-snug text-foreground" data-testid={`${tid}-text`}>
            {text}
          </span>
          <span className="block text-[11px] text-muted-foreground">Tap to ask about your job</span>
        </span>
        {/* tail */}
        <span
          className={`pointer-events-none absolute -bottom-[7px] h-3.5 w-3.5 rotate-45 border-b border-r border-border bg-card/95 ${right ? "right-5" : "left-5"}`}
          aria-hidden
        />
      </button>
      {onDismiss ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-[color,background-color] duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-steel focus-visible:ring-offset-1"
          aria-label="Dismiss greeting"
          data-testid={`${tid}-dismiss`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
};
